import { verifySplitPayment } from '@api/data';
import { Button, Icon, Link, Stack, Text, usePrevious } from '@chakra-ui/react';
import BigSectionTitleComponent from '@components/BigSectionTitleComponent/BigSectionTitleComponent';
import CheckboxComponent from '@components/CheckboxComponent/CheckboxComponent';
import InputComponent from '@components/InputComponent/InputComponent';
import RadioComponent from '@components/RadioComponent/RadioComponent';
import SelectComponent from '@components/SelectComponent/SelectComponent';
import SelectInputComponent from '@components/SelectInputComponent/SelectInputComponent';
import SmallSectionTitleComponent from '@components/SmallSectionTitleComponent/SmallSectionTitleComponent';
import UploadComponent from '@components/UploadComponent/UploadComponent';
import VatManualValidation from '@components/VatManualValidation';
import { StoreContext } from '@contexts/StoreContext';
import palette from '@theme/assets/palette';
import { DEFAULT_EXTRA_UE_VAT, NOTE_EU_VAT } from '@utils/constants';
import { SupplierNation } from '@utils/enum/SupplierNation';
import {
    defaultValidationNoRequiredInput,
    fileToBase64,
    getApplicantInfoFromAccount,
    validateEmail,
    validateNonSpecialString,
    validatePec,
    validatePhone,
    validateSdi,
} from '@utils/functions';
import { euCountries, extraEuCountries, prefixes, provinces } from '@utils/models/DataSelect';
import { FC, createRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
// import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { MdArrowRightAlt } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { CustomerForm, FieldsChecked } from 'types/formData';
import { ComboInputObject, RadioItem } from 'types/formElement';
import { StoreContextType } from 'types/reducers';
import { useMsal } from '@azure/msal-react';

interface Props {
    customerForm: CustomerForm;
    nextStep: (newForm: CustomerForm) => void;
    supplierNation: SupplierNation;
}

const Step1: FC<Props> = ({ customerForm, supplierNation, nextStep }) => {
    const recaptchaRef = createRef<ReCAPTCHA>();
    const { state } = useContext<StoreContextType>(StoreContext);
    const { accounts } = useMsal();
    const hasPrefilledApplicant = useRef(false);
    const [formDataCustomer, setFormDataCustomer] = useState<CustomerForm>(customerForm);
    const {
        handleSubmit,
        register,
        control,
        trigger,
        watch,
        setValue,
        setError,
        formState: { errors, isSubmitting, isValid },
    } = useForm({ mode: 'onBlur', criteriaMode: 'all' });
    const [selectedBilling, setSelectedBilling] = useState<string>(
        customerForm.invoice.sdi ? 'sdi' : customerForm.invoice.pec ? 'pec' : '',
    );
    const [splitPaymentDisabled, setSplitPaymentDisabled] = useState(false);
    const [vatValidation, setVatValidation] = useState<string[]>([]);
    const [taxIdCodeValidation, setTaxIdCodeValidation] = useState<string[]>([]);
    const [pecValidation, setPecValidation] = useState<string[]>([]);
    const [vatManualValidationStatus, setVatManualValidationStatus] = useState<boolean | null>(null);
    const vatWatcher = watch('vat');
    const vatGroupWatcher = watch('vatGroup');
    const taxIdCodeWatcher = watch('taxIdCode');
    const navigate = useNavigate();
    const previousCountrySelected = usePrevious(formDataCustomer.legalAddress.country);
    // const previousVat = usePrevious(formDataCustomer.vat);
    // const previousVatGroup = usePrevious(formDataCustomer.vatGroup);
    const previousTaxIdCode = usePrevious(formDataCustomer.taxIdCode);

    useEffect(() => {
        if (customerForm && supplierNation === SupplierNation.EXTRA_EU_COUNTRY) {
            setValue('vat', DEFAULT_EXTRA_UE_VAT);
        }
    }, [customerForm]);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        setVatValidation([...(customerForm.validations?.vatValidations ?? [])]);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        setTaxIdCodeValidation([...(customerForm.validations?.taxIdCodeValidations ?? [])]);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        setPecValidation([...(customerForm.validations?.pecValidations ?? [])]);
        setFormDataCustomer(customerForm);
    }, [customerForm]);

    const isRequiredVat: boolean = useMemo(() => {
        if (vatGroupWatcher === '') {
            if (supplierNation === SupplierNation.EU_COUNTRY) {
                return true;
            }
            if (supplierNation === SupplierNation.ITALY && taxIdCodeWatcher === '') {
                return true;
            }
        }
        return false;
    }, [supplierNation, taxIdCodeWatcher, vatGroupWatcher]);

    const isRequiredVatGroup: boolean = useMemo(() => {
        if (vatWatcher === '') {
            if (supplierNation === SupplierNation.EU_COUNTRY) {
                return true;
            }
            if (supplierNation === SupplierNation.ITALY && taxIdCodeWatcher === '') {
                return true;
            }
        }
        return false;
    }, [supplierNation, taxIdCodeWatcher, vatWatcher]);

    useEffect(() => {
        if (formDataCustomer.vat) {
            trigger('vatGroup');
        }
    }, [isRequiredVat]);

    useEffect(() => {
        if (formDataCustomer.vatGroup) {
            trigger('vat');
        }
    }, [isRequiredVatGroup]);

    const billingRadioItems: RadioItem[] = useMemo(() => {
        return [
            {
                id: 'sdi',
                label: 'SDI',
            },
            {
                id: 'pec',
                label: 'PEC',
            },
        ];
    }, []);

    const booleanRadioOptions: RadioItem[] = useMemo(() => {
        return [
            {
                id: 'true',
                label: 'Sì',
            },
            {
                id: 'false',
                label: 'No',
            },
        ];
    }, []);

    const booleanRegularExporterOptions: RadioItem[] = useMemo(() => {
        return [
            {
                id: 'true',
                label: 'Positivo',
            },
            {
                id: 'false',
                label: 'Negativo',
            },
        ];
    }, []);

    const onSubmit = useCallback(() => {
        trigger();
        if (isValid) {
            nextStep({
                ...formDataCustomer,
                validations: {
                    vatValidations: vatValidation,
                    taxIdCodeValidations: taxIdCodeValidation,
                    pecValidations: pecValidation,
                },
            });
        }
    }, [formDataCustomer, isValid, nextStep, pecValidation, taxIdCodeValidation, trigger, vatValidation]);

    const handleGoBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const handleChangeCompanyName = useCallback(
        (value: string) => {
            setFormDataCustomer({ ...formDataCustomer, companyName: value });
        },
        [formDataCustomer],
    );

    const handleSetPa = useCallback(
        (value: string) => {
            if (selectedBilling === 'sdi') {
                setFormDataCustomer({
                    ...formDataCustomer,
                    pa: value === 'true' ? true : false,
                    invoice: { ...formDataCustomer.invoice, sdi: value === 'true' ? '000000' : '0000000' },
                });
                setValue('sdi', value === 'true' ? '000000' : '0000000');
            } else {
                setFormDataCustomer({ ...formDataCustomer, pa: value === 'true' ? true : false });
            }
        },
        [formDataCustomer, selectedBilling, setValue],
    );

    const handleChangeVatNumber = useCallback(
        (value: string) => {
            if (supplierNation !== SupplierNation.EXTRA_EU_COUNTRY) {
                if (
                    (formDataCustomer.specialCustomsArrangements && supplierNation === SupplierNation.ITALY) ||
                    !formDataCustomer.specialCustomsArrangements
                ) {
                    setVatManualValidationStatus(null);
                }
            }
            setFormDataCustomer({
                ...formDataCustomer,
                vat: value,
                vatGroup: value !== '' ? '' : formDataCustomer.vatGroup,
            });
            if (value !== '' && formDataCustomer.vatGroup) {
                setValue('vatGroup', '');
                trigger('vatGroup');
            }
        },
        [formDataCustomer, setValue, trigger, supplierNation],
    );

    const handleChangeVatGroupNumber = useCallback(
        (value: string) => {
            if (supplierNation !== SupplierNation.EXTRA_EU_COUNTRY) {
                if (formDataCustomer.specialCustomsArrangements && supplierNation === SupplierNation.ITALY) {
                    setVatManualValidationStatus(null);
                }
            }
            setFormDataCustomer({
                ...formDataCustomer,
                vatGroup: value,
                vat:
                    value !== ''
                        ? ''
                        : supplierNation === SupplierNation.EXTRA_EU_COUNTRY
                          ? DEFAULT_EXTRA_UE_VAT
                          : formDataCustomer.vat,
            });
            if (value !== '' && formDataCustomer.vat) {
                setValue('vat', '');
                trigger('vat');
            } else {
                if (supplierNation === SupplierNation.EXTRA_EU_COUNTRY) {
                    setValue('vat', DEFAULT_EXTRA_UE_VAT);
                }
            }
        },
        [formDataCustomer, setValue, trigger, supplierNation],
    );

    const handleChangeTaxIdCodeNumber = useCallback(
        (value: string) => {
            setFormDataCustomer({ ...formDataCustomer, taxIdCode: value });
            if (supplierNation !== SupplierNation.EXTRA_EU_COUNTRY) {
                setVatManualValidationStatus(null);
            }
        },
        [formDataCustomer, supplierNation],
    );

    const handleChangeLegalAddress = useCallback(
        (value: string, key: 'address' | 'city' | 'zip' | 'province' | 'buildingNumber' | 'country') => {
            if (key === 'country') {
                if (supplierNation !== SupplierNation.EXTRA_EU_COUNTRY) {
                    setVatManualValidationStatus(null);
                }
            }
            if (key && formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    countryTax: key === 'country' ? value : formDataCustomer.countryTax,
                    legalAddress: {
                        ...formDataCustomer?.legalAddress,
                        [key]: value,
                    },
                });
                setTimeout(() => {
                    if (vatGroupWatcher !== '') {
                        trigger('vatGroup');
                    }
                    if (supplierNation !== SupplierNation.EXTRA_EU_COUNTRY) {
                        if (vatWatcher !== '') {
                            trigger('vat');
                        }
                    }
                }, 0);
            }
        },
        [formDataCustomer, supplierNation, trigger, vatGroupWatcher, vatWatcher],
    );

    // useEffect(() => {
    //     if (vatGroupWatcher !== '') {
    //         trigger('vatGroup');
    //     }
    // }, [formDataCustomer.legalAddress.country]);

    const handleChangePhoneNumber = useCallback(
        (values: ComboInputObject) => {
            if (
                /* (values.inputValue || values.selectValue) && */ formDataCustomer &&
                formDataCustomer.contact.phoneNumber
            ) {
                const foundPrefix = prefixes.find((prefix) => prefix.id === values.selectValue.toString());
                setFormDataCustomer({
                    ...formDataCustomer,
                    contact: {
                        ...formDataCustomer?.contact,
                        phoneNumber: {
                            prefix: foundPrefix ? foundPrefix.label : '',
                            number: values.inputValue,
                        },
                    },
                });
            }
        },
        [formDataCustomer],
    );

    const handleChangePecCompany = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    contact: {
                        ...formDataCustomer.contact,
                        pecCompany: value,
                    },
                });
            }
        },
        [formDataCustomer],
    );

    const handleChangeAccountingDocsEmail = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    contact: {
                        ...formDataCustomer.contact,
                        accountingDocsEmail: value,
                    },
                });
            }
        },
        [formDataCustomer],
    );

    const handleChangeManagerEmail = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    contact: {
                        ...formDataCustomer.contact,
                        managerEmail: value,
                    },
                });
            }
        },
        [formDataCustomer],
    );

    const handleSetSelectedBilling = useCallback(
        (selected: string | number) => {
            if (formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    invoice: {
                        ...formDataCustomer.invoice,
                        sdi: selected === 'sdi' ? (formDataCustomer.pa ? '000000' : '0000000') : null,
                        pec: selected === 'pec' ? '' : null,
                    },
                });
                setSelectedBilling(selected as string);
            }
        },
        [formDataCustomer, setSelectedBilling],
    );

    const handleChangeSdi = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    invoice: {
                        ...formDataCustomer.invoice,
                        sdi: value,
                    },
                });
            }
        },
        [formDataCustomer, setFormDataCustomer],
    );

    const handleChangeCig = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    invoice: {
                        ...formDataCustomer.invoice,
                        cig: value,
                    },
                });
            }
        },
        [formDataCustomer, setFormDataCustomer],
    );

    const handleChangeCup = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    invoice: {
                        ...formDataCustomer.invoice,
                        cup: value,
                    },
                });
            }
        },
        [formDataCustomer, setFormDataCustomer],
    );

    const handleChangePec = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    invoice: {
                        ...formDataCustomer.invoice,
                        pec: value,
                    },
                });
            }
        },
        [formDataCustomer, setFormDataCustomer],
    );

    const handleSetSplitPayment = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    invoice: {
                        ...formDataCustomer.invoice,
                        splitPayment: value === 'true' ? true : false,
                    },
                });
            }
        },
        [formDataCustomer],
    );

    const handleChangeCompanyGroup = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    invoice: {
                        ...formDataCustomer.invoice,
                        companyGroup: value,
                    },
                });
            }
        },
        [formDataCustomer],
    );

    const handleSetRegularExporter = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    invoice: {
                        ...formDataCustomer.invoice,
                        regularExporter: value === 'true' ? true : false,
                    },
                });
            }
        },
        [formDataCustomer],
    );

    const handleSetRegularExporterCheck = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    invoice: {
                        ...formDataCustomer.invoice,
                        regularExporterCheck: value === 'true' ? true : false,
                    },
                });
            }
        },
        [formDataCustomer],
    );

    const handleSetInternationalSubject72 = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    invoice: {
                        ...formDataCustomer.invoice,
                        internationalSubject72: value === 'true' ? true : false,
                    },
                });
            }
        },
        [formDataCustomer],
    );

    const handleSetCsvRecap = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    invoice: {
                        ...formDataCustomer.invoice,
                        csvRecap: value === 'true' ? true : false,
                    },
                });
            }
        },
        [formDataCustomer],
    );

    const handleOptionalEmailField = useCallback((value: string): string | boolean => {
        if (value) {
            return validateEmail(value);
        } else {
            return defaultValidationNoRequiredInput();
        }
    }, []);

    const handleUpdateExemptionLetter = useCallback(
        async (files: File[]) => {
            if (files) {
                const typeId = state.attachmentTypes.find((at) => at.tag === 'EXEMPTION_LETTER')?.id ?? 0;
                const attachments = [];
                for (let i = 0; i < files.length; i++) {
                    const filestream = (await fileToBase64(files[0])) as string;
                    const name = files[0].name;
                    const size = files[0].size;
                    const mimeType = files[0].type;
                    attachments.push({ filestream: filestream.split(',')[1], name, size, mimeType, typeId });
                }
                setFormDataCustomer({
                    ...formDataCustomer,
                    invoice: {
                        ...formDataCustomer.invoice,
                        exemptionLetter: {
                            attachments,
                        },
                    },
                });
            }
        },
        [formDataCustomer, state.attachmentTypes],
    );

    const handleDeleteExemptionLetter = useCallback(() => {
        if (formDataCustomer) {
            setFormDataCustomer({
                ...formDataCustomer,
                invoice: {
                    ...formDataCustomer.invoice,
                    exemptionLetter: {
                        attachments: [],
                    },
                },
            });
        }
    }, [formDataCustomer]);

    const validateSplitPayment = useCallback(
        async (code: string) => {
            if (recaptchaRef && recaptchaRef.current) {
                const splitPaymentOk = await verifySplitPayment(code, recaptchaRef.current);
                setFormDataCustomer({
                    ...formDataCustomer,
                    invoice: {
                        ...formDataCustomer.invoice,
                        splitPayment: splitPaymentOk as boolean,
                    },
                });
                setSplitPaymentDisabled(true);
            }
        },
        [formDataCustomer, recaptchaRef],
    );

    const vatLength = useMemo(() => {
        if (supplierNation === SupplierNation.ITALY) {
            return 11;
        } else {
            return undefined;
        }
    }, [supplierNation]);

    const legalAddressSelectComponent = useMemo(() => {
        if (supplierNation && formDataCustomer) {
            if (supplierNation === SupplierNation.ITALY) {
                return (
                    <SelectComponent
                        id="legalAddressProvince"
                        label="Provincia"
                        valueComponent={formDataCustomer.legalAddress.province}
                        options={provinces}
                        onChangeComponent={(value) => {
                            handleChangeLegalAddress(value as string, 'province');
                        }}
                        flex={1}
                        isRequired
                        isInvalid={errors.legalAddressProvince ?? null}
                        control={control}
                    />
                );
            } else {
                const countriesToSelect =
                    supplierNation === SupplierNation.EU_COUNTRY
                        ? euCountries.filter((country) => country.id !== 'IT')
                        : extraEuCountries;
                return (
                    <SelectComponent
                        id="legalAddressCountry"
                        label="Nazione"
                        valueComponent={formDataCustomer.legalAddress.country}
                        options={countriesToSelect}
                        onChangeComponent={(value) => {
                            handleChangeLegalAddress(value as string, 'country');
                        }}
                        flex={1}
                        isRequired
                        isInvalid={errors.legalAddressCountry ?? null}
                        control={control}
                    />
                );
            }
        }
        return null;
    }, [
        supplierNation,
        formDataCustomer,
        errors.legalAddressProvince,
        errors.legalAddressCountry,
        control,
        handleChangeLegalAddress,
    ]);

    const prefixId: string = useMemo(() => {
        if (formDataCustomer && formDataCustomer.contact.phoneNumber?.prefix) {
            const foundPrefix = prefixes.find(
                (prefix) => prefix.label === formDataCustomer.contact.phoneNumber?.prefix,
            );
            if (foundPrefix) {
                return foundPrefix.id;
            }
        }
        return '';
    }, [formDataCustomer]);

    const billingData = useMemo(() => {
        if (formDataCustomer) {
            return (
                <>
                    <BigSectionTitleComponent title="Fatturazione" />
                    {supplierNation === SupplierNation.ITALY ? (
                        <>
                            <RadioComponent
                                items={billingRadioItems}
                                id="billingSelect"
                                register={register}
                                name="Riferimento fatturazione elettronica"
                                onChange={handleSetSelectedBilling}
                                selected={selectedBilling}
                                direction="column"
                            />
                            {selectedBilling ? (
                                <Stack
                                    direction="row"
                                    justify="center"
                                    align="center"
                                    spacing="30px"
                                    alignSelf="stretch"
                                >
                                    {selectedBilling === 'sdi' || formDataCustomer.invoice.sdi ? (
                                        <InputComponent
                                            id="sdi"
                                            label="SDI"
                                            register={register}
                                            value={
                                                formDataCustomer.invoice.sdi
                                                    ? (formDataCustomer.invoice.sdi ?? '')
                                                    : formDataCustomer.pa
                                                      ? '000000'
                                                      : '0000000'
                                            }
                                            onChange={handleChangeSdi}
                                            isInvalid={errors.sdi ?? null}
                                            minLength={formDataCustomer.pa ? 6 : 7}
                                            maxLength={formDataCustomer.pa ? 6 : 7}
                                            isRequired
                                            validate={(value) => validateSdi(value, formDataCustomer.pa)}
                                        />
                                    ) : null}
                                    {selectedBilling === 'pec' || formDataCustomer.invoice.pec ? (
                                        <InputComponent
                                            id="pec"
                                            label="PEC"
                                            register={register}
                                            value={formDataCustomer.invoice.pec ?? ''}
                                            onChange={handleChangePec}
                                            isInvalid={errors.pec ?? null}
                                            maxLength={80}
                                            isRequired
                                            validate={(value) => {
                                                return conditionalValidation(value, 'pec');
                                            }}
                                        />
                                    ) : null}
                                </Stack>
                            ) : null}
                            <RadioComponent
                                items={booleanRadioOptions}
                                id="splitPayment"
                                register={register}
                                name="Cliente soggetto a SplitPayment"
                                onChange={handleSetSplitPayment}
                                selected={formDataCustomer.invoice.splitPayment.toString()}
                                direction="column"
                                // disabled={true}
                            />
                        </>
                    ) : null}

                    {/* <RadioComponent
                        items={booleanRadioOptions}
                        id="splitPayment"
                        register={register}
                        name="Cliente soggetto a SplitPayment"
                        onChange={handleSetSplitPayment}
                        selected={formDataCustomer.invoice.splitPayment.toString()}
                        direction="column"
                        disabled={splitPaymentDisabled}
                    /> */}
                    {formDataCustomer.pa ? (
                        <>
                            <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                                <InputComponent
                                    id="cig"
                                    label="Codice CIG**"
                                    register={register}
                                    value={formDataCustomer.invoice.cig ?? ''}
                                    onChange={handleChangeCig}
                                    isInvalid={errors.cig ?? null}
                                    minLength={10}
                                    maxLength={10}
                                    isRequired
                                />
                                <Text color="black" variant="subtitle" flex={1} paddingBottom="10px">
                                    **solo se previsto in fase di gara
                                </Text>
                            </Stack>
                            <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                                <InputComponent
                                    id="cup"
                                    label="Codice CUP"
                                    register={register}
                                    value={formDataCustomer.invoice.cup ?? ''}
                                    onChange={handleChangeCup}
                                    isInvalid={errors.cup ?? null}
                                    minLength={15}
                                    maxLength={15}
                                />
                                <Stack flex={1} />
                            </Stack>
                        </>
                    ) : null}
                    <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                        <InputComponent
                            id="InvoiceCompanyGroup"
                            label="Gruppo aziendale"
                            register={register}
                            value={formDataCustomer.invoice.companyGroup ?? ''}
                            onChange={handleChangeCompanyGroup}
                            isInvalid={errors.InvoiceCompanyGroup ?? null}
                        />
                    </Stack>
                    {supplierNation === SupplierNation.ITALY ? (
                        <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                            <RadioComponent
                                items={booleanRadioOptions}
                                id="regularExporter"
                                register={register}
                                name="Esportatore abituale"
                                onChange={handleSetRegularExporter}
                                selected={formDataCustomer.invoice.regularExporter ? 'true' : 'false'}
                                direction="column"
                            />
                        </Stack>
                    ) : null}
                    {formDataCustomer.invoice.regularExporter ? (
                        <Stack
                            direction="column"
                            justify="center"
                            align="center"
                            spacing="20px"
                            mb={30}
                            alignSelf="stretch"
                        >
                            <SmallSectionTitleComponent title={'Verifica ricevuta dichiarazione di intento'} />
                            <Text>
                                <Link
                                    color={palette.brand01}
                                    href="https://telematici.agenziaentrate.gov.it/VerIntent/VerificaIntent.do?evento=carica"
                                    target="_blank"
                                >
                                    Clicca qui per procedere alla verifica
                                </Link>
                            </Text>
                            <RadioComponent
                                items={booleanRegularExporterOptions}
                                id="regularExporterCheck"
                                register={register}
                                name="Esito"
                                onChange={handleSetRegularExporterCheck}
                                selected={formDataCustomer.invoice.regularExporterCheck ? 'true' : 'false'}
                                direction="column"
                            />
                        </Stack>
                    ) : null}
                    <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                        <RadioComponent
                            items={booleanRadioOptions}
                            id="internationalSubject72"
                            register={register}
                            name="Soggetto internazionale art. 72"
                            onChange={handleSetInternationalSubject72}
                            selected={formDataCustomer.invoice.internationalSubject72 ? 'true' : 'false'}
                            direction="column"
                        />
                    </Stack>
                    {formDataCustomer.invoice.internationalSubject72 ? (
                        <UploadComponent
                            id="exemptionLetter"
                            title="Lettera di esenzione"
                            subtitle="Carica lettera di esenzione:"
                            uploadedFile={formDataCustomer.invoice.exemptionLetter.attachments}
                            updateFile={handleUpdateExemptionLetter}
                            deleteFile={handleDeleteExemptionLetter}
                            control={control}
                            maxSize={1048576}
                            sizeString="1 MB"
                        />
                    ) : null}
                    <SmallSectionTitleComponent title="Documenti" />
                    <RadioComponent
                        items={booleanRadioOptions}
                        id="csvRecap"
                        register={register}
                        name="Il cliente desidera ricevere un file .CSV riepilogativo dei movimenti fatturati nel mese?"
                        onChange={handleSetCsvRecap}
                        selected={formDataCustomer.invoice.csvRecap ? 'true' : 'false'}
                        direction="column"
                    />
                </>
            );
        }
        return null;
    }, [
        formDataCustomer,
        supplierNation,
        billingRadioItems,
        register,
        handleSetSelectedBilling,
        selectedBilling,
        handleChangeSdi,
        handleChangeCig,
        errors.sdi,
        errors.pec,
        errors.cig,
        errors.cup,
        errors.InvoiceCompanyGroup,
        handleChangePec,
        booleanRadioOptions,
        handleSetSplitPayment,
        handleChangeCompanyGroup,
        handleSetRegularExporter,
        handleSetInternationalSubject72,
        handleUpdateExemptionLetter,
        control,
        handleSetCsvRecap,
    ]);

    const conditionalValidation = useCallback(
        async (value: string, field: string) => {
            if (recaptchaRef && recaptchaRef.current) {
                // if ((field === 'vat' || field === 'vatGroup') && !formDataCustomer.specialCustomsArrangements) {
                //     // const isChangedValue =
                //     //     field === 'vat'
                //     //         ? previousVat !== undefined && previousVat !== value
                //     //         : previousVatGroup !== undefined && previousVatGroup !== value;
                //     if (value !== '' && previousCountrySelected !== undefined) {
                //         const valid = await validateVat(
                //             value,
                //             supplierNation === SupplierNation.ITALY ? 'it' : formDataCustomer.legalAddress.country,
                //             recaptchaRef.current,
                //         );
                //         if (
                //             field === 'vatGroup' &&
                //             valid !== true &&
                //             supplierNation === SupplierNation.EXTRA_EU_COUNTRY
                //         ) {
                //             setValue('vat', DEFAULT_EXTRA_UE_VAT);
                //             setFormDataCustomer({
                //                 ...formDataCustomer,
                //                 vat: DEFAULT_EXTRA_UE_VAT,
                //             });
                //         }
                //         if (valid === true) {
                //             if (field === 'vat') {
                //                 if (!vatGroupWatcher && errors.vatGroup) {
                //                     trigger('vatGroup');
                //                 }
                //             }
                //             if (field === 'vatGroup') {
                //                 if (!vatWatcher && errors.vat) {
                //                     trigger('vat');
                //                 }
                //             }
                //             if (!taxIdCodeWatcher && errors.taxIdCode) {
                //                 trigger('taxIdCode');
                //             }
                //             setVatValidation([...vatValidation, value]);
                //             // validateSplitPayment(value);
                //         }
                //         return valid;
                //     }
                // }
                // if (field === 'taxIdCode') {
                //     if (value !== '' && previousTaxIdCode !== undefined) {
                //         const valid = await validateTaxIdCode(
                //             value,
                //             supplierNation === SupplierNation.ITALY ? 'it' : formDataCustomer.legalAddress.country,
                //             recaptchaRef.current,
                //             formDataCustomer.specialCustomsArrangements,
                //             vatGroupWatcher === '' && vatWatcher === '',
                //         );
                //         if (valid === true) {
                //             setTaxIdCodeValidation([...taxIdCodeValidation, value]);
                //             if (!vatGroupWatcher && errors.vatGroup) {
                //                 trigger('vatGroup');
                //             }
                //             if (!vatWatcher && errors.vat) {
                //                 trigger('vat');
                //             }
                //             validateSplitPayment(value);
                //         }
                //         return valid;
                //     }
                // }
                if (field === 'pec' || field === 'pecCompany') {
                    if (
                        value !== '' &&
                        !pecValidation.includes(value) &&
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        ![...(customerForm.validations?.pecValidations ?? [])].includes(value)
                    ) {
                        const valid = await validatePec(value, recaptchaRef.current);
                        if (valid === true) {
                            setPecValidation([...pecValidation, value]);
                        }
                        return valid;
                    }
                }

                return true;
            }
        },
        [
            vatValidation,
            customerForm.validations?.vatValidations,
            customerForm.validations?.taxIdCodeValidations,
            customerForm.validations?.pecValidations,
            formDataCustomer,
            supplierNation,
            setValue,
            taxIdCodeWatcher,
            errors,
            validateSplitPayment,
            vatGroupWatcher,
            trigger,
            vatWatcher,
            taxIdCodeValidation,
            pecValidation,
            recaptchaRef,
        ],
    );

    const checkVatValidation = useCallback(
        (fieldsValidation: FieldsChecked) => {
            if (fieldsValidation.vat === false || typeof fieldsValidation.vat === 'string') {
                setError('vat', { message: fieldsValidation.vat === false ? '' : fieldsValidation.vat });
            }

            if (fieldsValidation.vatGroup === false || typeof fieldsValidation.vatGroup === 'string') {
                if (supplierNation === SupplierNation.EXTRA_EU_COUNTRY) {
                    setValue('vat', DEFAULT_EXTRA_UE_VAT);
                    setFormDataCustomer({
                        ...formDataCustomer,
                        vat: DEFAULT_EXTRA_UE_VAT,
                    });
                }
                setError('vatGroup', { message: fieldsValidation.vatGroup === false ? '' : fieldsValidation.vatGroup });
            }

            if (fieldsValidation.taxIdCode === false || typeof fieldsValidation.taxIdCode === 'string') {
                setError('taxIdCode', {
                    message: fieldsValidation.taxIdCode === false ? '' : fieldsValidation.taxIdCode,
                });
            }
            if (fieldsValidation.taxIdCode === true) {
                validateSplitPayment(formDataCustomer.taxIdCode);
            }

            if (
                (fieldsValidation.vat === true && fieldsValidation.vatGroup == null) ||
                (fieldsValidation.vatGroup === true && fieldsValidation.vat == null) ||
                (fieldsValidation.vatGroup == null && fieldsValidation.vat == null)
            ) {
                if (fieldsValidation.taxIdCode === true || fieldsValidation.taxIdCode == null) {
                    setVatManualValidationStatus(true);
                    return;
                }
            }
            setVatManualValidationStatus(false);
        },
        [formDataCustomer, setError, setValue, supplierNation, validateSplitPayment],
    );

    const handleSetSpecialCustomsArrangements = useCallback(() => {
        if (formDataCustomer) {
            setFormDataCustomer({
                ...formDataCustomer,
                specialCustomsArrangements: !formDataCustomer.specialCustomsArrangements,
            });

            setVatManualValidationStatus(null);
        }
    }, [formDataCustomer]);

    const handleChangeApplicantName = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({ ...formDataCustomer, applicantName: value });
            }
        },
        [formDataCustomer, setFormDataCustomer],
    );

    const handleChangeApplicantSurname = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({ ...formDataCustomer, applicantSurname: value });
            }
        },
        [formDataCustomer, setFormDataCustomer],
    );

    const handleChangeApplicantEmail = useCallback(
        (value: string) => {
            if (formDataCustomer) {
                setFormDataCustomer({ ...formDataCustomer, applicantEmail: value });
            }
        },
        [formDataCustomer, setFormDataCustomer],
    );

    /* const disabledNextButton = useMemo(() => {
        const errorsKeys = Object.keys(errors);
        if (errorsKeys.length > 0) {
            return true;
        }
        if (!isValid) {
            return true;
        }
        return false;
    }, [errors, isValid, formDataCustomer]); */

    const renderVatValidationButton = useMemo(() => {
        if (formDataCustomer) {
            if (supplierNation !== SupplierNation.ITALY && formDataCustomer?.specialCustomsArrangements) {
                return null;
            }
            if (supplierNation === SupplierNation.EXTRA_EU_COUNTRY) {
                return null;
            }
            return (
                <VatManualValidation
                    vat={formDataCustomer.vat}
                    vatGroup={formDataCustomer.vatGroup}
                    taxIdCode={formDataCustomer.taxIdCode}
                    specialCustomsArrangements={formDataCustomer.specialCustomsArrangements}
                    country={supplierNation === SupplierNation.ITALY ? 'it' : formDataCustomer.legalAddress.country}
                    status={vatManualValidationStatus}
                    onValidate={checkVatValidation}
                    recaptchaRef={recaptchaRef}
                    requiredTaxIdCode={vatGroupWatcher === '' && vatWatcher === ''}
                />
            );
        }
    }, [
        checkVatValidation,
        formDataCustomer,
        recaptchaRef,
        supplierNation,
        vatGroupWatcher,
        vatManualValidationStatus,
        vatWatcher,
    ]);

    useEffect(() => {
        if (formDataCustomer?.specialCustomsArrangements && formDataCustomer?.supplierNation !== SupplierNation.ITALY) {
            setVatManualValidationStatus(true);
        } else {
            setVatManualValidationStatus(null);
        }
        // if (formDataCustomer.vat) {
        //     trigger('vat');
        // }
        // if (formDataCustomer.vatGroup) {
        //     trigger('vatGroup');
        // }
        // if (formDataCustomer.taxIdCode) {
        //     trigger('taxIdCode');
        // }
    }, [formDataCustomer?.specialCustomsArrangements]);

    // Prefill "Richiedente" (name/surname/email) from the logged-in Entra account — runs once,
    // never overwrites fields the user (or a previous visit to this step) already filled in.
    // The inputs are RHF-registered (uncontrolled via `register`'s ref), so updating
    // formDataCustomer alone does not repaint the DOM value — `setValue` is required too
    // (same pattern used elsewhere in this file, e.g. the EXTRA_EU vat prefill above).
    useEffect(() => {
        const account = accounts[0];
        if (hasPrefilledApplicant.current || !account) {
            return;
        }
        hasPrefilledApplicant.current = true;

        const applicantInfo = getApplicantInfoFromAccount(account);
        setFormDataCustomer((prev) => {
            const next = {
                ...prev,
                applicantName: prev.applicantName || applicantInfo.applicantName,
                applicantSurname: prev.applicantSurname || applicantInfo.applicantSurname,
                applicantEmail: prev.applicantEmail || applicantInfo.applicantEmail,
            };
            setValue('applicantName', next.applicantName);
            setValue('applicantSurname', next.applicantSurname);
            setValue('applicantEmail', next.applicantEmail);
            return next;
        });
    }, [accounts, setValue]);

    useEffect(() => {
        if (supplierNation === SupplierNation.EXTRA_EU_COUNTRY) {
            setVatManualValidationStatus(true);
        }
    }, [supplierNation]);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Stack
                paddingX="20px"
                paddingTop="20px"
                paddingBottom="20px"
                justify="center"
                align="center"
                spacing="20px"
                width="900px"
                maxWidth="100%"
                margin="0 auto"
            >
                <BigSectionTitleComponent title="Richiedente" />
                <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                    <InputComponent
                        id="applicantName"
                        label="Nome"
                        register={register}
                        value={formDataCustomer.applicantName ?? ''}
                        onChange={handleChangeApplicantName}
                        isInvalid={errors.applicantName ?? null}
                        maxLength={80}
                        isRequired
                    />
                    <InputComponent
                        id="applicantSurname"
                        label="Cognome"
                        register={register}
                        value={formDataCustomer.applicantSurname ?? ''}
                        onChange={handleChangeApplicantSurname}
                        isInvalid={errors.applicantSurname ?? null}
                        isRequired
                        maxLength={80}
                    />
                </Stack>
                <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                    <InputComponent
                        id="applicantEmail"
                        label="Email"
                        register={register}
                        value={formDataCustomer.applicantEmail ?? ''}
                        onChange={handleChangeApplicantEmail}
                        isInvalid={errors.applicantEmail ?? null}
                        isRequired
                        type="email"
                        validate={validateEmail}
                    />
                </Stack>
            </Stack>
            <Stack
                paddingX="20px"
                paddingTop="20px"
                paddingBottom="60px"
                justify="center"
                align="center"
                spacing="20px"
                width="900px"
                maxWidth="100%"
                margin="0 auto"
            >
                <BigSectionTitleComponent title="Anagrafica" />
                <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                    <InputComponent
                        id="companyName"
                        label="Ragione sociale"
                        register={register}
                        value={formDataCustomer.companyName}
                        onChange={handleChangeCompanyName}
                        isInvalid={errors.companyName ?? null}
                        minLength={5}
                        maxLength={80}
                        isRequired
                        validate={validateNonSpecialString}
                    />

                    {supplierNation !== SupplierNation.ITALY ? legalAddressSelectComponent : null}
                </Stack>
                {supplierNation === SupplierNation.ITALY || supplierNation === SupplierNation.EU_COUNTRY ? (
                    <CheckboxComponent
                        id={'specialCustomsArrangements'}
                        name="Regime doganale speciale"
                        selected={formDataCustomer.specialCustomsArrangements}
                        register={register}
                        onChange={handleSetSpecialCustomsArrangements}
                    />
                ) : null}
                {supplierNation === SupplierNation.ITALY ? (
                    <RadioComponent
                        id="pa"
                        selected={formDataCustomer.pa ? 'true' : 'false'}
                        register={register}
                        items={booleanRadioOptions}
                        direction="column"
                        onChange={handleSetPa}
                        name="Pubblica amministrazione"
                        isRequired
                    />
                ) : null}
                {supplierNation === SupplierNation.EU_COUNTRY && (
                    <Text fontStyle="italic" fontSize="14px" color="#999">
                        {NOTE_EU_VAT}
                    </Text>
                )}
                <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                    <InputComponent
                        id="vat"
                        label="Partita IVA"
                        register={register}
                        value={formDataCustomer.vat ?? ''}
                        onChange={handleChangeVatNumber}
                        isInvalid={errors.vat ?? null}
                        isRequired={isRequiredVat}
                        maxLength={vatLength}
                        minLength={vatLength}
                        isDisabled={supplierNation === SupplierNation.EXTRA_EU_COUNTRY}
                        // validate={(value) => {
                        //     return conditionalValidation(value, 'vat');
                        // }}
                    />
                    <InputComponent
                        id="vatGroup"
                        label="Partita IVA del gruppo"
                        register={register}
                        value={formDataCustomer.vatGroup ?? ''}
                        onChange={handleChangeVatGroupNumber}
                        isInvalid={errors.vatGroup ?? null}
                        isRequired={isRequiredVatGroup}
                        maxLength={vatLength}
                        minLength={vatLength}
                        // validate={(value) => {
                        //     return conditionalValidation(value, 'vatGroup');
                        // }}
                    />
                </Stack>
                {supplierNation === SupplierNation.ITALY ? (
                    <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                        <InputComponent
                            id="taxIdCode"
                            label="Codice Fiscale"
                            register={register}
                            value={formDataCustomer.taxIdCode ?? ''}
                            onChange={handleChangeTaxIdCodeNumber}
                            isInvalid={errors.taxIdCode ?? null}
                            isRequired={vatWatcher === '' && vatGroupWatcher === ''}
                            minLength={11}
                            maxLength={16}
                            // validate={(value) => {
                            //     return conditionalValidation(value, 'taxIdCode');
                            // }}
                        />
                    </Stack>
                ) : null}
                {renderVatValidationButton}
                <SmallSectionTitleComponent title="Indirizzo" />
                <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                    <InputComponent
                        id="legalAddressAddress"
                        label="Indirizzo sede legale"
                        register={register}
                        value={formDataCustomer.legalAddress.address ?? ''}
                        onChange={(value) => handleChangeLegalAddress(value, 'address')}
                        isInvalid={errors.legalAddressAddress ?? null}
                        isRequired
                        flex={3.4}
                    />
                    <InputComponent
                        id="legalAddressBuildingNumber"
                        label="Numero civico"
                        register={register}
                        value={formDataCustomer.legalAddress.buildingNumber ?? ''}
                        onChange={(value) => handleChangeLegalAddress(value, 'buildingNumber')}
                        isInvalid={errors.legalAddressBuildingNumber ?? null}
                        isRequired
                        flex={1}
                    />
                </Stack>
                <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                    <InputComponent
                        id="legalAddressCity"
                        label="Città"
                        register={register}
                        value={formDataCustomer.legalAddress.city ?? ''}
                        onChange={(value) => handleChangeLegalAddress(value, 'city')}
                        isInvalid={errors.legalAddressCity ?? null}
                        flex={2.2}
                        isRequired
                    />
                    <InputComponent
                        id="legalAddressZip"
                        label="CAP"
                        register={register}
                        value={formDataCustomer.legalAddress.zip ?? ''}
                        onChange={(value) => handleChangeLegalAddress(value, 'zip')}
                        isInvalid={errors.legalAddressZip ?? null}
                        flex={1}
                        minLength={supplierNation === SupplierNation.ITALY ? 5 : undefined}
                        maxLength={supplierNation === SupplierNation.ITALY ? 5 : undefined}
                        isRequired
                    />
                    {supplierNation === SupplierNation.ITALY ? legalAddressSelectComponent : null}
                </Stack>
                <SmallSectionTitleComponent title="Contatti" />
                <Stack
                    direction="row"
                    justify="center"
                    align="center"
                    spacing="30px"
                    alignSelf={supplierNation === SupplierNation.ITALY ? 'stretch' : 'flex-start'}
                >
                    <SelectInputComponent
                        labelSelect="Prefisso"
                        labelInput="Telefono"
                        options={prefixes.map((prefix) => {
                            return {
                                ...prefix,
                                label: `(${prefix.value}) ${prefix.label}`,
                            };
                        })}
                        value={{
                            inputValue: formDataCustomer.contact.phoneNumber?.number ?? '',
                            selectValue: prefixId ?? 'IT',
                        }}
                        onChange={handleChangePhoneNumber}
                        registerInput={register}
                        controlSelect={control}
                        isInvalidInput={errors.telefono ?? null}
                        inputType="tel"
                        maxInputLength={15}
                        minInputLength={6}
                        validateInput={validatePhone}
                        trigger={trigger}
                        watch={watch}
                    />
                    {supplierNation === SupplierNation.ITALY ? (
                        <InputComponent
                            id="pecCompany"
                            label="PEC aziendale"
                            register={register}
                            value={formDataCustomer.contact.pecCompany ?? ''}
                            onChange={handleChangePecCompany}
                            isInvalid={errors.pecCompany ?? null}
                            maxLength={80}
                            validate={(value) => {
                                return conditionalValidation(value, 'pecCompany');
                            }}
                            isRequired={vatWatcher !== '' || vatGroupWatcher !== ''}
                        />
                    ) : null}
                </Stack>
                <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                    <InputComponent
                        id="accountingDocsEmail"
                        label="Email per documenti contabili"
                        register={register}
                        value={formDataCustomer.contact.accountingDocsEmail ?? ''}
                        onChange={handleChangeAccountingDocsEmail}
                        isInvalid={errors.accountingDocsEmail ?? null}
                        maxLength={80}
                        isRequired
                        validate={validateEmail}
                    />
                    <InputComponent
                        id="managerEmail"
                        label="Email responsabile amministrativo"
                        register={register}
                        value={formDataCustomer.contact.managerEmail ?? ''}
                        onChange={handleChangeManagerEmail}
                        isInvalid={errors.managerEmail ?? null}
                        maxLength={80}
                        validate={handleOptionalEmailField}
                    />
                </Stack>
                {billingData}
                <Stack
                    paddingTop="20px"
                    justify="left"
                    align="left"
                    spacing="20px"
                    width="900px"
                    maxWidth="100%"
                    margin="0 auto"
                >
                    <Text variant="subtitle" opacity={0.6}>
                        * campi obbligatori.
                    </Text>
                </Stack>
                {!vatManualValidationStatus && (
                    <Text textAlign="center" variant="subtitle" color={'red'}>
                        Completa la validazione dei campi IVA/Codice Fiscale cliccando sul pulsante{' '}
                        {supplierNation === SupplierNation.ITALY ? 'Convalida P. IVA/C.F.' : 'Convalida P.IVA'}.
                    </Text>
                )}
                <Stack
                    direction="row"
                    justify="center"
                    align="center"
                    spacing="10px"
                    alignSelf="stretch"
                    padding="20px 0"
                    marginBottom="60px"
                >
                    <Button
                        size="lg"
                        variant={'outline'}
                        borderColor={palette.brand01}
                        color={palette.brand01}
                        onClick={handleGoBack}
                    >
                        Annulla
                    </Button>
                    <Button
                        rightIcon={<Icon as={MdArrowRightAlt} />}
                        size="lg"
                        variant={'main'}
                        bg={palette.brand01}
                        isLoading={isSubmitting}
                        type="submit"
                        isDisabled={!vatManualValidationStatus}
                    >
                        Avanti
                    </Button>
                </Stack>
            </Stack>
            <ReCAPTCHA
                size="invisible"
                theme="dark"
                sitekey={window.GattinoniAnagraphicForm.RECAPTCHA_PUBLIC_KEY}
                ref={recaptchaRef}
            />
        </form>
    );
};

export default Step1;
